import { useState, useCallback, useRef } from 'react'
import {
  executeSchedulingRequest,
  SchedulingResultType,
} from '@abroad/rest-client'

const useSchedulingRestrictions = ({
  onSuccess,
  onSameDayConflict,
  onSessionErrors,
  onError,
}) => {
  const [weeklyModal, setWeeklyModal] = useState({
    isOpen: false,
    message: '',
  })
  const [isConfirmingWeekly, setIsConfirmingWeekly] = useState(false)
  const pendingRetryRef = useRef(null)

  const closeWeeklyModal = useCallback(() => {
    setWeeklyModal({ isOpen: false, message: '' })
    pendingRetryRef.current = null
    setIsConfirmingWeekly(false)
  }, [])

  const handleSchedulingResult = useCallback(
    async (result) => {
      if (result.type === SchedulingResultType.SUCCESS) {
        closeWeeklyModal()
        if (onSuccess) {
          onSuccess(result.data)
        }
        return { success: true, data: result.data }
      }

      if (result.type === SchedulingResultType.SAME_DAY_CONFLICT) {
        closeWeeklyModal()
        if (onSameDayConflict) {
          onSameDayConflict(result.message)
        }
        return { success: false, sameDayConflict: true }
      }

      if (result.type === SchedulingResultType.SESSION_ERRORS) {
        closeWeeklyModal()
        if (onSessionErrors) {
          onSessionErrors(result.perSessionErrors, result.message)
        } else if (onError) {
          onError(result.message)
        }
        return { success: false, sessionErrors: result.perSessionErrors }
      }

      if (result.type === SchedulingResultType.WEEKLY_WARNING) {
        pendingRetryRef.current = {
          apiFn: result.apiFn,
          payload: result.payload,
        }
        setWeeklyModal({
          isOpen: true,
          message: result.message,
        })
        return { success: false, weeklyWarning: true }
      }

      closeWeeklyModal()
      if (onError) {
        onError(result.message)
      }
      return { success: false }
    },
    [closeWeeklyModal, onSuccess, onSameDayConflict, onSessionErrors, onError],
  )

  const runScheduling = useCallback(
    async (apiFn, payload) => {
      const result = await executeSchedulingRequest(apiFn, payload)
      return handleSchedulingResult(result)
    },
    [handleSchedulingResult],
  )

  const handleWeeklyConfirm = useCallback(async () => {
    const pending = pendingRetryRef.current
    if (!pending?.apiFn) {
      closeWeeklyModal()
      return
    }

    try {
      setIsConfirmingWeekly(true)
      const result = await executeSchedulingRequest(pending.apiFn, {
        ...pending.payload,
        confirmWeeklyWarning: true,
      })
      await handleSchedulingResult(result)
    } finally {
      setIsConfirmingWeekly(false)
    }
  }, [closeWeeklyModal, handleSchedulingResult])

  return {
    weeklyModal,
    isConfirmingWeekly,
    closeWeeklyModal,
    handleWeeklyConfirm,
    runScheduling,
  }
}

export default useSchedulingRestrictions
